import { makeAutoObservable } from 'mobx';
import { ExpertDefaults } from '@ai/common/src/types/expert';
import { ExpertConfig } from '@ai/common/src/types/expert';
import { defineSampleRate, sendAccumulatedData, getPCMData } from '../utils/audioUtils';
import SpeechSynthesizerStore from './speechSynthesizerStore';
import { widgetService } from '../api/services/widget.service';
import { getDisableInterrupt } from '../utils/getDisableInterrupt';
import { DialogMode, MessageType } from '@ai/common/src/types/chat';
import { dialogService } from '@ai/common/src/api/services/dialogService';
import { v4 } from 'uuid';
import { ChatState } from "@ai/common/src/store/chat";

type SttData = {
  type: 'final_refinement' | 'started' | 'partial';
  alternatives?: string[] | null;
  error?: string;
  speech_dict: { start?: number; end?: number } | null;
};

export type SendMessageFunc = ({
  userMessage,
  messageType,
  enableService,
  proactiveMessage,
}: {
  userMessage: string;
  messageType: MessageType;
  enableService?: boolean | undefined;
  proactiveMessage?: string;
}) => Promise<void>;

export type WidgetState = 'idle' | 'listening' | 'initializing';

const BUFFER_DURATION = 0.016; // Длительность буфера в секундах, менять с большой осторожностью, так как это важно для VAD
const INACTIVE_TIME = 30_000; // время до автоматического завершения неактивного разговора
const SUPPORTED_SAMPLE_RATES = [8_000, 16_000]; // пересечение между поддерживаемыми значениями ЧД для VAD, распознавания и всех синтезов
export const TARGET_SAMPLE_RATE = 16_000;

export default class AudioStreamStore {
  volume = 0;
  error = '';
  widgetState: WidgetState = 'idle';

  private audioContext: AudioContext | null = null;
  private recognitionAudioWorkletNode: AudioWorkletNode | null = null;
  private synthesisAudioWorkletNode: AudioWorkletNode | null = null;
  private mediaStream: MediaStream | null = null;
  private recognitionWs: WebSocket | null = null;
  private synthesisRecordWs: WebSocket | null = null;
  private recognitionAudioBuffer: Float32Array[] = []; // Буфер для накопления аудиоданных
  private synthesisAudioBuffer: Float32Array[] = []; // Буфер для накопления аудиоданных
  sampleRate: number | undefined = undefined;

  private disableInterrupt: boolean;

  isFinalSpeech = false;
  isPlayingFirstSentence = false;
  private isFinishDialogRequestSent = false;

  speechSynthesizerStore!: SpeechSynthesizerStore;

  private conversationEndTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private expertId: string,
    private config: ExpertConfig | null,
    private constants: ExpertDefaults,
    public getDialogId: () => string | null,
    public setDialogId: (value: string | null) => void,
    public getDialogMode: () => DialogMode,
    public setDialogMode: (value: DialogMode) => void,
    public sendMessage: SendMessageFunc,
    public getContinuePreviousDialog: () => boolean,
    public getAbortController: () => AbortController,
    public getChatState: () => ChatState,
    private setChatState: (state: ChatState) => void,
  ) {
    this.initSpeechSynthesizerStore(config);
    this.disableInterrupt = getDisableInterrupt() && !!this.config?.safe_interrupt;

    makeAutoObservable(this, {}, { autoBind: true });
  }

  private initSpeechSynthesizerStore(config: ExpertConfig | null) {
    this.speechSynthesizerStore = new SpeechSynthesizerStore(config, this.constants);

    this.speechSynthesizerStore.onError = (message: string) => {
      this.setError(message);
      this.stopRecording();
    };

    this.speechSynthesizerStore.onAudioStartPlay = () => {
      if (this.widgetState === 'initializing') {
        this.widgetState = 'listening';
        // this.setIsInitializing(false);
      }
      this.setChatState('idle');
    };

    this.speechSynthesizerStore.onAudioStopPlay = () => {
      this.isPlayingFirstSentence = false;
      this.extendTimer();
      if (this.isFinalSpeech) {
        this.stopRecording();
      }
    };
  }

  changeExpertConfig = (config: ExpertConfig | null) => {
    this.config = config;
    this.disableInterrupt = getDisableInterrupt() && !!this.config?.safe_interrupt;
    this.initSpeechSynthesizerStore(config);
  };

  setVolume = (value: number) => (this.volume = value);
  setError = (value: string) => (this.error = value);
  setIsFinishDialogRequestSent = (value: boolean) => (this.isFinishDialogRequestSent = value);
  setSynthesisRecordWs = (ws: WebSocket | null) => (this.synthesisRecordWs = ws);

  extendTimer = () => {
    console.log('>>> EXTEND TIMER');
    if (this.conversationEndTimer) {
      clearTimeout(this.conversationEndTimer);
    }

    this.conversationEndTimer = setTimeout(() => {
      if (
        this.speechSynthesizerStore.audioPlayer?.isPlaying ||
        this.getChatState() === 'loading' ||
        this.getDialogMode() !== 'expert'
      ) {
        this.extendTimer();
      } else {
        console.log('>>> TIMED OUT');
        this.stopRecording();
      }
    }, INACTIVE_TIME);
  };

  // Инициализация аудио контекста и worklet
  private async initializeAudioContext(bufferSize: number) {
    try {
      this.audioContext = new AudioContext({
        sampleRate: this.sampleRate,
        latencyHint: 'interactive',
      });

      if (!this.audioContext.audioWorklet) {
        throw new Error('AudioWorklet is not supported in this browser.');
      }

      await this.audioContext.audioWorklet.addModule(
        `${widgetService.rootOrigin}/widget/static/audio-processor.js`,
      );

      this.recognitionAudioWorkletNode = new AudioWorkletNode(
        this.audioContext,
        'audio-processor',
        {
          processorOptions: {
            bufferSize: bufferSize,
          },
        },
      );

      this.synthesisAudioWorkletNode = new AudioWorkletNode(this.audioContext, 'audio-processor', {
        processorOptions: {
          bufferSize: bufferSize,
        },
      });

      return true;
    } catch (error) {
      console.error('Error initializing audio context:', error);
      if (error instanceof Error) {
        this.setError('Error initializing audio context: ' + error.message);
      }
      this.stopRecording();
      return false;
    }
  }

  // Инициализация WebSocket
  private async initializeWebSocket() {
    if (!navigator?.mediaDevices?.getUserMedia) {
      this.setError('Ваш браузер не поддерживает доступ к микрофону');
      this.stopRecording();

      return;
    }

    const res = await defineSampleRate(SUPPORTED_SAMPLE_RATES, TARGET_SAMPLE_RATE);
    if ('error' in res) {
      this.setError(res.error);
      this.stopRecording();
      return;
    } else {
      this.sampleRate = res.sampleRate;
      this.mediaStream = res.mediaStream;
    }

    this.recognitionWs = new WebSocket(`${widgetService.rootOrigin}/widget/api/v1/stt-stream-ws`);

    const recordWs = new WebSocket(
      `${widgetService.rootOrigin}/widget/api/v1/tts-record-ws?dialog_id=${this.getDialogId()}&sample_rate=${this.sampleRate}`,
    );
    this.setSynthesisRecordWs(recordWs);

    this.recognitionWs.onopen = () => {
      if (this.recognitionWs) {
        this.recognitionWs.send(
          JSON.stringify({
            expert_id: this.expertId,
            dialog_id: this.getDialogId(),
            sample_rate: this.sampleRate,
          }),
        );
      }

      // console.log('WebSocket connection established');
    };

    this.recognitionWs.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.setError('WebSocket error');
      this.stopRecording();
    };

    this.recognitionWs.onclose = () => {
      // console.log('WebSocket connection closed');
      this.stopRecording();
    };

    this.recognitionWs.onmessage = (event) => {
      this.handleWebSocketMessage(event);
    };
  }

  // Обработка сообщений от WebSocket
  private async handleWebSocketMessage(event: MessageEvent) {
    if (typeof event.data !== 'string') return;

    try {
      const data: SttData = JSON.parse(event.data);

      if (data.error) {
        console.error(data.error);
        this.setError(data.error);
        this.stopRecording();
      }

      switch (data.type) {
        case 'started':
          {
            await this.startAudioRecorder();
            const isEmptyFirstSentence =
              (typeof this.config?.first_sentense === 'string' && !this.config.first_sentense) ||
              (!this.config?.first_sentense && !this.constants.first_sentense);

            if (
              isEmptyFirstSentence ||
              this.getContinuePreviousDialog() ||
              this.getDialogMode() !== 'expert'
            ) {
              // this.setIsInitializing(false);
              this.widgetState = 'listening';
            } else {
              this.isPlayingFirstSentence = true;
              this.sendMessage({ userMessage: '/start', messageType: MessageType.VOICE });
            }
          }
          break;

        case 'final_refinement': {
          const text = data.alternatives?.[0] || '';
          const isSpeakingInNoInterruptMode =
            this.speechSynthesizerStore.audioPlayer?.isPlaying &&
            (this.disableInterrupt || this.isPlayingFirstSentence);

          if (text.length && !isSpeakingInNoInterruptMode && this.getChatState() !== 'loading') {
            // console.log('final_refinement: ', text);
            this.isFinalSpeech = false;
            this.getAbortController().abort();
            this.speechSynthesizerStore.audioPlayer?.stop();

            this.sendMessage({ userMessage: text, messageType: MessageType.VOICE });
          }
          break;
        }

        case 'partial':
          // console.log('######## partial: ', data.alternatives?.[0]);
          if (
            data.alternatives?.[0] &&
            // чтобы не перебивать случайными звуками ждем как минимум два слова
            data.alternatives?.[0].split(' ').length > 1
          ) {
            this.extendTimer();

            // обработка перебивания
            if (
              this.speechSynthesizerStore.audioPlayer?.isPlaying &&
              !this.disableInterrupt &&
              !this.isPlayingFirstSentence &&
              this.getChatState() !== 'loading'
            ) {
              // console.log('interrupt ', data.alternatives?.[0]);
              this.isFinalSpeech = false;
              this.getAbortController().abort();
              this.speechSynthesizerStore.audioPlayer?.stop();
            }
          }
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
      if (error instanceof Error) {
        this.setError('Error parsing WebSocket message: ' + error.message);
      }
    }
  }

  startAudioRecorder = async () => {
    if (this.widgetState === 'listening') return;

    try {
      if (!this.sampleRate) throw new Error('Sample rate is not defined');

      const bufferSize = this.sampleRate * BUFFER_DURATION;

      if (!(await this.initializeAudioContext(bufferSize))) return;
      if (!this.recognitionAudioWorkletNode) return;
      if (!this.synthesisAudioWorkletNode) return;
      if (!this.audioContext) return;
      if (!this.mediaStream) return;

      await this.speechSynthesizerStore.init(this.sampleRate);

      if (!this.speechSynthesizerStore.audioPlayer) return;

      const recognitionSource = this.audioContext.createMediaStreamSource(this.mediaStream);
      recognitionSource.connect(this.recognitionAudioWorkletNode);
      this.recognitionAudioWorkletNode.connect(this.audioContext.destination);

      const synthesisSource = this.audioContext.createMediaStreamSource(
        this.speechSynthesizerStore.audioPlayer.playbackStream,
      );
      synthesisSource.connect(this.synthesisAudioWorkletNode);
      this.synthesisAudioWorkletNode.connect(this.audioContext.destination);

      const analyser = this.audioContext.createAnalyser();
      recognitionSource.connect(analyser);
      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      this.recognitionAudioWorkletNode.port.onmessage = (event: MessageEvent<Float32Array>) => {
        if (this.widgetState !== 'listening') return;

        analyser.getByteFrequencyData(dataArray);
        const sum = dataArray.reduce((acc, value) => acc + value, 0);
        const average = sum / dataArray.length;
        this.setVolume(average);

        const audioData = event.data;
        const pcmData = getPCMData(audioData);
        const float32Data = new Float32Array(pcmData);

        this.recognitionAudioBuffer.push(float32Data);

        if (this.recognitionAudioBuffer.length * float32Data.length >= bufferSize) {
          sendAccumulatedData(this.recognitionAudioBuffer, this.recognitionWs);
        }
      };

      this.synthesisAudioWorkletNode.port.onmessage = (event: MessageEvent<Float32Array>) => {
        if (this.widgetState !== 'listening') return;

        const audioData = event.data;
        const pcmData = getPCMData(audioData);
        const float32Data = new Float32Array(pcmData);

        this.synthesisAudioBuffer.push(float32Data);

        if (this.synthesisAudioBuffer.length * float32Data.length >= bufferSize) {
          sendAccumulatedData(this.synthesisAudioBuffer, this.synthesisRecordWs);
        }
      };

      this.widgetState = 'listening';
      this.extendTimer();
      // console.log('Audio recording started.');
    } catch (error) {
      console.error('Error starting audio recording:', error);
      if (error instanceof Error) {
        this.setError('Error starting audio recording: ' + error.message);
      }
      this.stopRecording();
    }
  };

  // Запуск записи и WebSocket
  startRecording = async () => {
    if (this.widgetState === 'listening') {
      console.warn('Recording is already in progress.');
      return;
    }
    this.setError('');
    this.widgetState = 'initializing';
    this.setIsFinishDialogRequestSent(false);

    try {
      if (!this.getDialogId()) {
        const newDialogId = v4();
        this.setDialogId(newDialogId);
      }
      await this.initializeWebSocket();
    } catch (e) {
      this.setError(`WebSocket init error: ${e}`);
      this.stopRecording();
    }
  };

  finishDialog = () => {
    const dialogId = this.getDialogId();

    if (!dialogId) return;
    if (this.isFinishDialogRequestSent) {
      // console.log('Finish dialog request is already sent');
      return;
    }

    void dialogService.finishDialog(dialogId);
  };

  // Остановка записи
  stopRecording = () => {
    if (this.widgetState === 'idle' && this.getChatState() === 'idle') return;

    // this.setIsRecording(false);
    this.widgetState = 'idle';
    this.setChatState('idle');

    // !!
    // if (localStorage.getItem(this.storageKey)) {
    //   this.getContinuePreviousDialog() = true;
    // }

    this.finishDialog();

    this.getAbortController().abort();
    this.recognitionAudioBuffer = [];
    this.synthesisAudioBuffer = [];
    this.sampleRate = undefined;
    this.isFinalSpeech = false;

    this.speechSynthesizerStore.stop();

    if (this.recognitionWs?.readyState === WebSocket.OPEN) {
      this.recognitionWs.close();
      this.recognitionWs = null;
    }

    if (this.synthesisRecordWs?.readyState === WebSocket.OPEN) {
      this.synthesisRecordWs.close();
      this.setSynthesisRecordWs(null);
    }

    if (this.recognitionAudioWorkletNode) {
      this.recognitionAudioWorkletNode.port.onmessage = null;
      this.recognitionAudioWorkletNode.disconnect();
      this.recognitionAudioWorkletNode = null;
    }

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().then(() => {
        this.audioContext = null;
      });
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });

      this.mediaStream = null;
    }

    if (this.conversationEndTimer) {
      clearTimeout(this.conversationEndTimer);
      this.conversationEndTimer = null;
    }

    // console.log('Recording stopped.');
  };
}
